import React from 'react';
import { motion } from 'framer-motion';
import { FaUserTie, FaUniversity, FaChalkboardTeacher, FaHandshake, FaAward } from 'react-icons/fa';
import { GiDiploma } from 'react-icons/gi';
import { IoMdPeople } from 'react-icons/io';
import './leadership.css';

const Leadership = () => {
  const leadershipTeam = [
    {
      id: 1,
      name: "Dr. Wei Zhang",
      title: "President",
      bio: "Visionary leader with 25+ years in higher education and academic innovation",
      image: "/images/leadership/president.jpg", // Replace with actual image path
      achievements: [
        "Former Dean at National University of Singapore",
        "Pioneer in STEM education reform",
        "Author of 5 books on educational leadership"
      ]
    },
    {
      id: 2,
      name: "Prof. Mei Ling Chen",
      title: "Vice President, Academic Affairs",
      bio: "Expert in curriculum development and quality assurance in technical education",
      image: "/images/leadership/vp-academic.jpg",
      achievements: [
        "Led accreditation of 12 engineering programs",
        "Recipient of National Education Excellence Award",
        "PhD in Electrical Engineering from MIT"
      ]
    },
    {
      id: 3,
      name: "Dr. Raj Patel",
      title: "Vice President, Research",
      bio: "Research strategist with $50M+ in secured grants for interdisciplinary projects",
      image: "/images/leadership/vp-research.jpg",
      achievements: [
        "Former Director at Max Planck Institute",
        "Holder of 15 patents in materials science",
        "Named 'Top 100 Scientists in Asia' 2023"
      ]
    },
    {
      id: 4,
      name: "Ms. Aisha binti Mohamad",
      title: "Dean of Students",
      bio: "Student advocate focused on holistic development and campus life excellence",
      image: "/images/leadership/dean-students.jpg",
      achievements: [
        "Developed award-winning mentorship program",
        "20 years experience in student affairs",
        "Keynote speaker at international education conferences"
      ]
    }
  ];

  const advisoryBoard = [
    {
      name: "Dr. Hiroshi Tanaka",
      role: "Chairman, Tokyo Tech",
      expertise: "International academic collaboration"
    },
    {
      name: "Ms. Priya Desai",
      role: "CEO, TechSolutions Asia",
      expertise: "Industry-academia partnerships"
    },
    {
      name: "Prof. David Wilson",
      role: "Emeritus Professor, Stanford",
      expertise: "Educational technology"
    },
    {
      name: "Dr. Lin Yao",
      role: "Former Minister of Education",
      expertise: "Policy development"
    }
  ];

  const stats = [
    { value: "15:1", label: "Student to Faculty Ratio" },
    { value: "92%", label: "Faculty with Terminal Degrees" },
    { value: "40+", label: "Countries Represented" },
    { value: "100%", label: "Programs Accredited" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div 
      className="leadership-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <div className="leadership-hero">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1><FaUserTie /> Leadership at Asian Polytechnic</h1>
          <p className="hero-subtitle">Guiding the institution towards academic excellence and innovation</p>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.05 }}
                variants={itemVariants}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Leadership Team */}
      <section className="leadership-team">
        <div className="section-header">
          <h2><FaUniversity /> Executive Leadership</h2>
          <p>Our senior administrators shaping the future of the institution</p>
        </div>
        
        <motion.div 
          className="team-grid"
          variants={containerVariants}
        >
          {leadershipTeam.map((member) => (
            <motion.div 
              key={member.id}
              className="member-card"
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)"
              }}
            >
              <div className="member-image">
                <img src={member.image} alt={member.name} />
                <div className="image-overlay"></div>
              </div>
              <div className="member-info">
                <h3>{member.name}</h3>
                <p className="member-title">{member.title}</p>
                <p className="member-bio">{member.bio}</p>
                <div className="member-achievements">
                  <h4>Key Achievements:</h4>
                  <ul>
                    {member.achievements.map((achievement, index) => (
                      <li key={index}>{achievement}</li>
                    ))}
                  </ul>
                </div>
                <button className="profile-btn">View Full Profile</button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Advisory Board */}
      <section className="advisory-board">
        <div className="section-header">
          <h2><FaHandshake /> International Advisory Board</h2>
          <p>Global thought leaders guiding our strategic direction</p>
        </div>
        
        <motion.div 
          className="board-grid"
          variants={containerVariants}
        >
          {advisoryBoard.map((member, index) => (
            <motion.div 
              key={index}
              className="board-card"
              variants={itemVariants}
              whileHover={{ scale: 1.03 }}
            >
              <div className="board-icon">
                <IoMdPeople size={30} />
              </div>
              <h3>{member.name}</h3>
              <p className="board-role">{member.role}</p>
              <p className="board-expertise"><strong>Expertise:</strong> {member.expertise}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Governance */}
      <section className="governance-section">
        <motion.div 
          className="governance-content"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="governance-text">
            <h2><GiDiploma /> Governance & Accreditation</h2>
            <p>Asian Polytechnic operates under the highest standards of academic governance, with oversight from our Board of Trustees and accreditation from leading educational bodies worldwide.</p>
            <div className="accreditations">
              <div className="accreditation-badge">ABET</div>
              <div className="accreditation-badge">AACSB</div>
              <div className="accreditation-badge">WASC</div>
              <div className="accreditation-badge">ISO 21001</div>
            </div>
          </div>
          <div className="governance-image">
            {/* This would be an image in production */}
            <div className="image-placeholder"></div>
          </div>
        </motion.div>
      </section>

      {/* Faculty Leadership */}
      <section className="faculty-leadership">
        <div className="section-header">
          <h2><FaChalkboardTeacher /> Faculty Leadership</h2>
          <p>Department chairs and research center directors driving academic excellence</p>
        </div>
        
        <div className="faculty-highlights">
          <motion.div 
            className="highlight-card"
            whileHover={{ scale: 1.02 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaAward size={40} />
            <h3>15 Distinguished Professors</h3>
            <p>Recipients of national and international awards for teaching and research</p>
          </motion.div>
          
          <motion.div 
            className="highlight-card"
            whileHover={{ scale: 1.02 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FaUniversity size={40} />
            <h3>8 Research Centers</h3>
            <p>Interdisciplinary hubs for innovation and discovery</p>
          </motion.div>
          
          <motion.div 
            className="highlight-card"
            whileHover={{ scale: 1.02 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <FaHandshake size={40} />
            <h3>120+ Industry Partners</h3>
            <p>Collaborating on curriculum development and research projects</p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Leadership;